import { FC, ReactNode, useEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { useDebounce } from 'commons/hooks/common'
import { documentsListQueryOptions } from '~bank-documents/service/documentsListQueryOptions'
import { DocumentListFilterParams } from '~bank-documents/types/documentList'

import { DocumentsListContext } from './DocumentsListContext'

type DocumentsListProviderProps = {
  children: ReactNode
}

export const DocumentsListContextProvider: FC<DocumentsListProviderProps> = ({
  children
}: DocumentsListProviderProps) => {
  const routeApi = getRouteApi('/_app/documents')

  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  // state for showing pending state right after user begin typing
  const [isSearching, setIsSearching] = useState(false)

  const documentsListQuery = useQuery(documentsListQueryOptions(search))

  const debouncedSearch = useDebounce((searchParams: Partial<DocumentListFilterParams>) =>
    navigate({ to: '.', search: (prev) => ({ ...prev, ...searchParams }), resetScroll: false, replace: true })
  )

  useEffect(() => {
    // if search are done (fetched) and we do not have another debounced search lets cancel search state
    if (documentsListQuery.isFetched && !documentsListQuery.isRefetching && !debouncedSearch.pending()) {
      setIsSearching(() => false)
    }
    // and cancel debounced search, to stop navigation in case when user on another page already
    return () => {
      debouncedSearch.cancel()
    }
  }, [documentsListQuery.isFetched, documentsListQuery.isFetching, documentsListQuery.isRefetching, debouncedSearch])

  if (!documentsListQuery.data) return null

  const value = {
    data: documentsListQuery.data,
    isPending: documentsListQuery.isLoading || documentsListQuery.isRefetching || isSearching,
    successFullyLoaded: !!documentsListQuery.data,
    search,
    invalidate: documentsListQuery.refetch,
    onFilterChange: (searchParams: Partial<DocumentListFilterParams>) => {
      // Determine if we are searching or not. Maybe better to have onSearch prop right after onFilterChange?
      // @ts-ignore
      if (searchParams.search !== search.search) {
        setIsSearching(() => true)
        return debouncedSearch(searchParams)
      }
      return navigate({
        to: '.',
        search: () => searchParams,
        resetScroll: false,
        replace: true
      })
    },
    onPageChange: (page: number, size: number) =>
      navigate({
        to: '.',
        search: (prev: DocumentListFilterParams) => ({ ...prev, page, size }),
        resetScroll: false,
        replace: true
      }),
    clear: () => navigate({ to: '.', search: undefined, resetScroll: false, replace: true })
  }

  return <DocumentsListContext.Provider value={value}>{children}</DocumentsListContext.Provider>
}
